import { ArrowPathIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AiSchedule, AiScheduleRun, AiScheduleRunStatus, getAiScheduleRuns } from 'services'
import { Modal } from 'stories/components'
import { MarkdownRender } from 'stories/components/MarkdownRender/MarkdownRender'
import { formatTime } from 'utils'

/** While a run is still working, re-read it often enough to feel live without hammering the API. */
const POLL_MS = 4000

const STATUS_STYLE: Record<AiScheduleRunStatus, string> = {
  queued: 'bg-slate-100 text-slate-600',
  running: 'bg-sky-50 text-sky-700',
  success: 'bg-green-50 text-green-700',
  error: 'bg-red-50 text-red-700',
  skipped: 'bg-gray-100 text-gray-600',
}

const STATUS_LABEL: Record<AiScheduleRunStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  success: 'Done',
  error: 'Failed',
  skipped: 'Skipped',
}

const duration = (ms?: number) => {
  if (!ms) return ''
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`
}

interface ScheduleRunsModalProps {
  schedule: AiSchedule
  /** Jump to the chat a run wrote into, so its full transcript is one click away. */
  onOpenConversation?: (conversationId: number) => void
  onClose: () => void
}

/**
 * What this schedule has actually done: every attempt, newest first, including the ones that
 * failed - a run that threw never reaches a chat at all, so this is the only place it shows.
 *
 * It polls while a run is working, which is what makes "Run now" readable: that call returns as
 * soon as the run row exists, because an agentic turn can take minutes.
 */
export function ScheduleRunsModal({ schedule, onOpenConversation, onClose }: ScheduleRunsModalProps) {
  const [runs, setRuns] = useState<AiScheduleRun[]>([])
  const [activeId, setActiveId] = useState<number | null>(null)
  const [isLoading, setLoading] = useState(true)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(async () => {
    const data = await getAiScheduleRuns(schedule.id).catch(() => [] as AiScheduleRun[])
    setRuns(data || [])
    setLoading(false)
    // Keep following the newest run until it settles, so Run now shows its own answer arriving.
    setActiveId((prev) => (prev && (data || []).some((run) => run.id === prev) ? prev : data?.[0]?.id ?? null))
    return data || []
  }, [schedule.id])

  useEffect(() => {
    let stopped = false
    const tick = async () => {
      const data = await load()
      if (stopped) return
      // Queued counts as working: a fan-out writes every run up front and drains them in passes.
      if (data.some((run) => run.status === 'running' || run.status === 'queued')) {
        timer.current = setTimeout(tick, POLL_MS)
      }
    }
    tick()
    return () => {
      stopped = true
      if (timer.current) clearTimeout(timer.current)
    }
  }, [load])

  const active = runs.find((run) => run.id === activeId) || null

  return (
    <Modal title={`Runs - ${schedule.name}`} titleOkay="" titleCancel="Close" isOpen onClose={onClose}>
      <div className="w-[52rem] max-w-full">
        {!isLoading && !runs.length && (
          <p className="text-sm text-gray-500 py-6 text-center">
            This schedule has not run yet. Use Run now to try it without waiting for its next turn.
          </p>
        )}

        {!!runs.length && (
          <div className="flex gap-4 h-[26rem]">
            <div className="w-56 shrink-0 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
              {runs.map((run) => (
                <button
                  key={run.id}
                  className={`w-full text-left px-3 py-2 ${run.id === activeId ? 'bg-shade-blue/10' : 'hover:bg-gray-50'}`}
                  onClick={() => setActiveId(run.id)}
                >
                  <span className="flex items-center gap-1.5">
                    <span className={`text-[11px] font-semibold rounded px-1.5 py-0.5 ${STATUS_STYLE[run.status]}`}>
                      {STATUS_LABEL[run.status]}
                    </span>
                    {run.status === 'running' && <ArrowPathIcon className="w-3.5 h-3.5 animate-spin text-sky-600" />}
                    {!!run.loanNumber && <span className="text-[11px] text-gray-500">Loan {run.loanNumber}</span>}
                  </span>
                  <span className="block text-xs text-gray-600 mt-1">{formatTime(run.startedAt || run.createdAt)}</span>
                  <span className="block text-[11px] text-gray-400">
                    {run.trigger === 'manual' ? 'Run now' : 'On schedule'}
                    {!!run.durationMs && ` · ${duration(run.durationMs)}`}
                  </span>
                </button>
              ))}
            </div>

            <div className="flex-1 min-w-0 overflow-y-auto">
              {active && (
                <>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`text-xs font-semibold rounded px-2 py-0.5 ${STATUS_STYLE[active.status]}`}>
                      {STATUS_LABEL[active.status]}
                    </span>
                    {!!active.loanNumber && <span className="text-xs text-gray-500">Loan {active.loanNumber}</span>}
                    {!!active.conversationId && !!onOpenConversation && (
                      <button
                        className="text-xs text-shade-blue hover:underline flex items-center gap-1"
                        onClick={() => {
                          onOpenConversation(active.conversationId as number)
                          onClose()
                        }}
                      >
                        <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />
                        Open the chat
                      </button>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 whitespace-pre-wrap border-l-2 border-gray-200 pl-2 mb-3">
                    {active.prompt}
                  </p>

                  {(active.status === 'running' || active.status === 'queued') && (
                    <p className="text-sm text-gray-500 flex items-center gap-1.5">
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      {active.status === 'queued' ? 'Waiting its turn in the queue.' : 'Working on it - this can take a few minutes.'}
                    </p>
                  )}

                  {/* The error is shown even when an answer came with it: the "assistant is
                      unavailable" reply IS the answer in that case, and reading it as a result
                      rather than a failure is the whole problem. */}
                  {!!active.error && (
                    <p className="text-sm text-red-700 bg-red-50 rounded px-3 py-2 mb-3 whitespace-pre-wrap">
                      {active.error}
                    </p>
                  )}

                  {/* Not when the answer IS the error: a failure whose text is the assistant's own
                      "unavailable" reply would otherwise be printed twice. */}
                  {!!active.answer && active.answer !== active.error && (
                    <div className="text-sm text-gray-800">
                      <MarkdownRender text={active.answer} />
                    </div>
                  )}

                  {!active.answer && !active.error && active.status !== 'running' && active.status !== 'queued' && (
                    <p className="text-sm text-gray-500">This run produced no answer.</p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
